import { CAPACITY_MARKET_RESULT_STATUSES, Status } from '@/features/bidding/constants'
import type { BidOverviewSummaryCell } from '@/features/bidding/types/bidOverviewSummary'

export type CapacityStatusChip = {
  status: Status
  volume?: number | null
  percentage?: number
}

const isNoBid = (statuses: Status[]): boolean => statuses.length === 1 && statuses[0] === Status.NO_BID

const resolveAggregateStatus = (percentage: number): Status => {
  if (percentage > 100) return Status.OVERBID_ACCEPTED
  if (percentage >= 100) return Status.ACCEPTED
  if (percentage <= 0) return Status.REJECTED
  return Status.PARTIALLY_ACCEPTED
}

/**
 * Reduces a capacity cell (which aggregates the activation groups of a portfolio/market
 * program/delivery day) into the chips to render:
 * - A "No bid" chip when there are no bids.
 * - Once market results are in, a single aggregate chip derived from accepted/offered
 *   volume: 100% → Accepted, >100% → Overbid accepted, 0% → Rejected, otherwise
 *   Partially accepted (carries the %).
 * - Before results, the Offered chip (with the offered volume) plus every other status
 *   rendered as-is (e.g. Draft). Market-result statuses are handled above, so an
 *   unrecognised/new status surfaces as its own chip rather than being silently hidden.
 */
export const getCapacityStatusChips = (cell: BidOverviewSummaryCell): CapacityStatusChip[] => {
  const { uniqueStatuses, totalOfferedVolume, totalAcceptedVolume } = cell

  if (isNoBid(uniqueStatuses)) {
    return [{ status: Status.NO_BID }]
  }

  const hasMarketResults = uniqueStatuses.some((status) => CAPACITY_MARKET_RESULT_STATUSES.includes(status))

  if (hasMarketResults) {
    const percentage = totalOfferedVolume ? ((totalAcceptedVolume ?? 0) / totalOfferedVolume) * 100 : 0
    return [{ status: resolveAggregateStatus(percentage), volume: totalAcceptedVolume, percentage }]
  }

  const chips: CapacityStatusChip[] = []
  if (uniqueStatuses.includes(Status.OFFERED)) {
    chips.push({ status: Status.OFFERED, volume: totalOfferedVolume })
  }
  uniqueStatuses.filter((status) => status !== Status.OFFERED).forEach((status) => chips.push({ status }))
  return chips
}
